import React from 'react';
import {statementApi} from "../../../backend/StatementApi";
import LoaderWithCompanySelected from "../../../components/LoaderWithCompanySelected";
import SendTransactionsModal from "./SendTransactionsModal";
import ButtonWithConfirmation from "../../../components/button/ButtonWithConfirmation";
import {ReadStatementDto} from "../../../model/statement/ReadStatementDto";
import MatchedDocument, {BaseMatchedTransaction, GlTransaction, MatchingResultType, TxType} from "../../../model/MatchedDocument";
import {transactionsExchangeStore} from "../../../store/TransactionsExchange";
import {analyzedTransactionApi} from "../../../backend/AnalyzedTransactionApi";
import {Accordion, AccordionDetails, AccordionSummary, Button, Grid, Typography} from "@mui/material";
import TwoSidedPage from "../../../components/TwoSidedPage";
import {companyAccountsStore} from "../../../store/CompanyAccounts";
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import SendIcon from '@mui/icons-material/Send';
import StrikethroughSIcon from '@mui/icons-material/StrikethroughS';
import PrintIcon from '@mui/icons-material/Print';
import CodeIcon from '@mui/icons-material/Code';
import TransactionTablesComponent from "./TransactionTablesComponent";
import {ActionMap} from "../../../utils/ActionMap";
import {StatementStateEnum} from "../../../model/statement/StatementStateEnum";
import {Toggle} from "../../../components/input/Toggle";
import LabelImportantIcon from '@mui/icons-material/LabelImportant';
import AccountCodeSelectionModal from "./AccountCodeSelectionModal";
import {transactionOverrideStore} from "../../../store/TransactionOverride";
import {BankAccountCode} from "../../../model/BankAccountCode";
import {Nil} from "../../../utils/Types";
import {notificationStore} from "../../../store/NotificationStore";
import {ResultOrErrorDto} from "../../../model/ResultOrErrorDto";
import {TextFieldWithPersistence} from "../../../components/input/TextFieldWithPersistence";

export type SelectedTransactions = Map<MatchingResultType, BaseMatchedTransaction[]>

interface IProps {
    id: string
}

interface IState {
    statement?: ReadStatementDto,
    loading: boolean,
    matchingResult?: MatchedDocument,
    selection: SelectedTransactions,
    showHiddenColumns: boolean,
    showSendTxsDialog: boolean,
    showAccountCodeSelectionDialog: boolean,
    sendTxAction?: (tx: BaseMatchedTransaction) => Promise<ResultOrErrorDto<boolean>>,
    expandedTable?: MatchingResultType
}

const STATEMENT_STATE_CHANGES = new ActionMap<StatementStateEnum, [string], Promise<void>>()
        .on(StatementStateEnum.ARCHIVED).do(id => statementApi.unarchiveStatement(id))
        .on(StatementStateEnum.OPEN).do(id => statementApi.archiveStatement(id))
        .selector()


export default class SingleStatement extends React.Component<IProps, IState> {


    constructor(props: IProps) {
        super(props);
        this.state = {
            loading: true,
            selection: new Map<MatchingResultType, BaseMatchedTransaction[]>(),
            showHiddenColumns: false,
            showSendTxsDialog: false,
            showAccountCodeSelectionDialog: false,
        };
    }

    async componentDidMount() {
        await companyAccountsStore.refresh();
        const statementDto = await statementApi.fetchStatement(this.props.id);
        this.setState({
            statement: statementDto,
            loading: false
        })
    }

    render() {
        const {loading, matchingResult, selection, showSendTxsDialog, showAccountCodeSelectionDialog, sendTxAction, statement} = this.state;
        const allSelectedElements = this.getAllSelectedElements(selection);
        const bankDetails = statement ? companyAccountsStore.findByIban(statement.iban, statement.currency) : null;
        return <LoaderWithCompanySelected loading={loading}>
            <SendTransactionsModal show={showSendTxsDialog}
                                   selectedItems={allSelectedElements}
                                   sendItem={sendTxAction!!} onClose={this.onTransactionsSent}/>


            {statement &&
                    <>
                        {showAccountCodeSelectionDialog &&
                                <AccountCodeSelectionModal itemsToOverride={allSelectedElements}
                                                           onOverride={this.doOverrideTransactionsAsGL(allSelectedElements, bankDetails!!)}
                                                           onClose={this.closeAccountCodeSelectModal}/>
                        }
                        <TwoSidedPage
                                leftPanelSize={2}
                                leftPanel={this.renderToolbox(statement, bankDetails, loading, allSelectedElements)}
                                rightPanelSize={10}
                                rightPanel={<>{matchingResult && this.renderMatchedResult(statement, matchingResult.transactions)}</>}
                        />
                    </>
            }
        </LoaderWithCompanySelected>;
    }

    private closeAccountCodeSelectModal = () => {
        this.setState({showAccountCodeSelectionDialog: false});
    }

    private doOverrideTransactionsAsGL = (itemsToOverride: BaseMatchedTransaction[], bankDetails: BankAccountCode) => (accCode: string) => {
        itemsToOverride.forEach(it => {

            const [accountCodeDebit, accountCodeCredit] = it.tx.txType === TxType.DBIT ?
                    [accCode, bankDetails.accountCode] :
                    [bankDetails.accountCode, accCode]

            const override: GlTransaction = {
                tx: it.tx,
                type: MatchingResultType.GL_RULE,
                accountCodeDebit,
                accountCodeCredit,
            };
            transactionOverrideStore.setTransaction(bankDetails.iban, override)
        })

        this.closeAccountCodeSelectModal()
        this.setState({
            loading: true
        }, () => {
            this.setState({
                loading: false,
                selection: new Map<MatchingResultType, BaseMatchedTransaction[]>()
            })
        });
    }

    private getAllSelectedElements = (selection: Map<MatchingResultType, BaseMatchedTransaction[]>) => {
        return Array.from(selection.values()).flatMap(it => it);
    }

    private renderToolbox(statement: ReadStatementDto, bankDetails: BankAccountCode | Nil, loading: boolean, selection: BaseMatchedTransaction[]) {
        const selectedCount = selection.length;
        const bankName = bankDetails ? bankDetails.name : "";

        return <>
            <Typography variant={"h6"}>{bankName}</Typography>
            <Typography variant={"subtitle1"}>{statement.iban}</Typography>
            <Typography variant={"subtitle1"}>{statement.periodFrom.toLocaleDateString()}</Typography>
            <Typography variant={"subtitle1"}>{statement.periodTo.toLocaleDateString()}</Typography>
            <Button
                    variant="contained"
                    fullWidth
                    disabled={loading}
                    onClick={this.startAnalysis}
                    startIcon={<DirectionsRunIcon/>}>
                Run analysis
            </Button>
            <hr/>
            <Typography variant={"h6"}>Selected: {selectedCount}</Typography>
            <hr/>
            <Button variant="contained" color="primary" fullWidth onClick={this.markTransactionsAsGL}
                    disabled={selectedCount <= 0} startIcon={<LabelImportantIcon/>}>
                Link to GL record
            </Button>
            <hr/>
            <ButtonWithConfirmation variant="contained" color="primary" fullWidth
                                    onClick={this.sendToMerit} disabled={selectedCount <= 0} startIcon={<SendIcon/>}>
                Send to merit
            </ButtonWithConfirmation>
            <ButtonWithConfirmation variant="contained" color="secondary" fullWidth
                                    onClick={this.markAsSent} disabled={selectedCount <= 0}
                                    startIcon={<StrikethroughSIcon/>}>
                Mark as sent
            </ButtonWithConfirmation>
            <hr/>
            <Button variant="contained" color="primary" fullWidth onClick={this.openPreview}
                    disabled={selectedCount <= 0} startIcon={<PrintIcon/>}>
                Open preview for printing
            </Button>
            <hr/>
            <Button variant="outlined" fullWidth onClick={this.toggleHiddenColumns} startIcon={<CodeIcon/>}>
                {this.state.showHiddenColumns ? "Hide dev columns" : "Show dev columns"}
            </Button>
            <hr/>
            <TextFieldWithPersistence
                    name="comment" label="Comment"
                    placeholder="Comment related to statement"
                    multiline
                    minRows={10}
                    maxRows={10}
                    fullWidth={true}
                    value={statement.comment}
                    onPersist={async value => {
                        await statementApi.saveComment(statement.id, value)
                        this.setState({
                            statement: {...statement, comment: value}
                        })
                    }}
            />
            <hr/>
            <Toggle
                    checked={this.state.statement?.state === StatementStateEnum.ARCHIVED}
                    onChange={this.changeArchiveStatus}
            >
                Archived
            </Toggle>
        </>;
    }

    private handleChange = (item: MatchingResultType) => (event: React.ChangeEvent<{}>, isExpanded: boolean) => {
        this.setState({
            expandedTable: isExpanded ? item : undefined
        })
    };

    private renderMatchedResult(statement: ReadStatementDto, transactions: BaseMatchedTransaction[]) {
        if (transactions && transactions.length > 0) {
            const bankDetails = this.findBankDetails(statement)
            return (
                    <TransactionTablesComponent
                            bank={bankDetails}
                            showActions={true}
                            selectionListener={(type: MatchingResultType, selection: BaseMatchedTransaction[]) => {
                                const newSelection = new Map(this.state.selection)
                                newSelection.set(type, selection)
                                this.setState({selection: newSelection})
                            }}
                            showHiddenColumns={this.state.showHiddenColumns}
                            transactions={transactions}
                            itemWrapper={
                                (description, table, type) =>
                                        <Accordion key={description} onChange={this.handleChange(type)}
                                                   expanded={this.state.expandedTable === type}>
                                            <AccordionSummary>
                                                {description}
                                            </AccordionSummary>
                                            <AccordionDetails>
                                                {table}
                                            </AccordionDetails>
                                        </Accordion>
                            }>
                    </TransactionTablesComponent>
            );
        } else {
            return (
                    <Grid container>
                        <Grid item container alignItems="center" justifyContent="center" xs={12}>
                            <Typography variant={"h5"}>No transactions in this statement</Typography>
                        </Grid>
                    </Grid>
            );
        }
    }

    private findBankDetails = (statement: ReadStatementDto) => {
        return companyAccountsStore.findByIban(statement.iban, statement.currency)!!;
    }

    private startAnalysis = () => {
        this.setState({
            loading: true
        });
        statementApi.analyzeStatement(this.props.id)
                .then((data) => {
                    this.setState({
                        matchingResult: data,
                        loading: false,
                        selection: new Map<MatchingResultType, BaseMatchedTransaction[]>()
                    })
                }).catch(() => {
            this.setState({loading: false})
            notificationStore.addNotification("Failed to analyze statement", {variant: "error"})
        })
    };

    private onTransactionsSent = (success: boolean) => {
        this.setState({
            showSendTxsDialog: false
        });
        if (success) {
            this.startAnalysis();
        }
    };

    openPreview = () => {
        const origin = window.location.origin
        const selection = this.getAllSelectedElements(this.state.selection)
        const id = transactionsExchangeStore.setTransactions(this.findBankDetails(this.state.statement!!), selection)
        window.open(origin + "/preview/tx/" + id, '_blank');
    };

    markAsSent = () => {
        this.setState({
            showSendTxsDialog: true,
            sendTxAction: async tx => {
                await analyzedTransactionApi.markAsSent([tx])
                return {result: true}
            }
        });
    };

    sendToMerit = () => {
        this.setState({
            showSendTxsDialog: true,
            sendTxAction: tx => analyzedTransactionApi.send(tx)
        });
    };

    markTransactionsAsGL = () => {
        this.setState({
            showAccountCodeSelectionDialog: true,
        });
    };

    toggleHiddenColumns = () => {
        this.setState({
            showHiddenColumns: !this.state.showHiddenColumns,
            selection: new Map<MatchingResultType, BaseMatchedTransaction[]>()
        })
    };

    changeArchiveStatus = async () => {
        await STATEMENT_STATE_CHANGES.for(this.state.statement!.state).execute(this.props.id);
        const statement = await statementApi.fetchStatement(this.props.id);
        this.setState({statement})
    };
}